import React, { useEffect, useState } from 'react';

import { IconPlus } from '@tabler/icons-react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Typography } from '@cfa/react-components';
import { Accordion } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';

import {
  IntlCategoryDTO,
  IntlSubcategoryDTO,
  IntlSubtitleDTO,
} from '@/services/content-api-types';
import { LanguageObject } from '@/types/types';

import Constants from 'constants/index';

import EditSection from './EditSection';
import Header from './Header';
import SubtitleDragAndDrop from './SubtitleDragAndDrop';

import { DocumentDto } from '@/_generated/api-clients/xp';
import { userPrefsAtom } from '@/atoms/userPrefs';
import { useFocusAdminState } from '@/routes/Admin/state';

export interface SubtitleProps {
  index: number;
  name: LanguageObject;
  documents?: DocumentDto[];
  enabled?: boolean;
  visibleDocuments?: DocumentDto[];
}
interface SubcategoryAccordionProps {
  adminCategoryId: null | string;
  category: IntlCategoryDTO;
  countryCode: string;
  index: number;
  isAddProcedurePopUp?: boolean;
  isCanada: boolean;
  isUK: boolean;
  onSaveSubtitle: (
    pendingSectionMutation: { name: LanguageObject },
    index: number,
  ) => void;
  onSaveSubtitlesOrder: (
    updatedSubtitles: IntlSubtitleDTO[],
    index: number,
  ) => void;
  openInNewTab?: boolean;
  showAdminFunctionality: boolean;
  subcategory: IntlSubcategoryDTO;
  turnOffAllEditing: number;
  setEnableCategoryButtonsFromChildren: (value: boolean) => void;
  translationRequired: boolean;
}

export const SubcategoryAccordion: React.FC<SubcategoryAccordionProps> = ({
  adminCategoryId,
  countryCode,
  index,
  isAddProcedurePopUp,
  isCanada,
  isUK,
  onSaveSubtitle,
  onSaveSubtitlesOrder,
  openInNewTab,
  showAdminFunctionality,
  subcategory,
  turnOffAllEditing,
  setEnableCategoryButtonsFromChildren,
  translationRequired,
}) => {
  const { t } = useTranslation();
  const { data } = useAuth();
  const highestPermissionLevel = data?.highestPermissionLevel;
  const { name: subcategoryName, subtitles } = subcategory;
  const { selectedSubcategoryId, setSelectedSubcategoryId } =
    useFocusAdminState('selectedSubcategoryId');
  const { selectedSubtitle, setSelectedSubtitle } =
    useFocusAdminState('selectedSubtitle');

  const userPrefs = useAtomValue(userPrefsAtom);

  const initialAddSectionState = {
    name: { en: '', es: '' },
  };
  const [pendingSectionMutation, setPendingSectionMutation] = useState(
    initialAddSectionState,
  );
  const [sectionEditType, setSectionEditType] = useState<
    'add' | 'edit' | undefined
  >(undefined);
  const [addSectionFormIsValid, setAddSectionFormIsValid] = useState(false);

  let userIsAdmin = false;

  if (showAdminFunctionality) {
    userIsAdmin = Constants.USER_PERMISSIONS.ADMIN === highestPermissionLevel;
  }

  const safeAdminCategoryId = adminCategoryId ?? '';
  const openDocumentsInNewTab = openInNewTab ?? false;

  useEffect(() => {
    if ((isCanada || isUK) && pendingSectionMutation.name.en) {
      setAddSectionFormIsValid(true);
    } else if (
      pendingSectionMutation.name.en.length &&
      pendingSectionMutation.name.es.length
    ) {
      setAddSectionFormIsValid(true);
    } else {
      setAddSectionFormIsValid(false);
    }
  }, [pendingSectionMutation, isCanada, isUK, countryCode]);

  useEffect(() => {
    setPendingSectionMutation({
      name: { en: '', es: '' },
    });
  }, [selectedSubcategoryId]);

  useEffect(() => {
    setSectionEditType(undefined);
    setEnableCategoryButtonsFromChildren(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnOffAllEditing]);

  useEffect(() => {
    if (sectionEditType === 'edit') {
      setEnableCategoryButtonsFromChildren(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionEditType]);

  const handleAddSectionCancel = () => {
    setPendingSectionMutation(initialAddSectionState);
    setSectionEditType(undefined);
    setEnableCategoryButtonsFromChildren(false);
  };

  const handleAddSection = () => {
    setSelectedSubtitle({
      ...selectedSubtitle,
      documents: [],
      visibleDocuments: [],
    } as IntlSubtitleDTO);
    setPendingSectionMutation(initialAddSectionState);
    setSectionEditType('add');
  };

  return (
    <Accordion.Root
      data-testid={`Subcategory${index}`}
      onExpandedChange={(keys: Set<React.Key>) => {
        if (keys.size > 0) {
          setSelectedSubcategoryId(subcategory.id);
        } else if (!isAddProcedurePopUp) {
          setSelectedSubcategoryId(undefined);
        }
      }}
    >
      <Accordion.Item data-testid={`SubTitle${index}`}>
        <Accordion.Trigger>
          <Header
            countryCode={countryCode}
            index={index}
            showAdminFunctionality={showAdminFunctionality}
            subcategoryName={subcategoryName}
            userIsAdmin={userIsAdmin}
            subcategory={subcategory}
            turnOffAllEditing={turnOffAllEditing}
            setEnableCategoryButtonsFromChildren={
              setEnableCategoryButtonsFromChildren
            }
            translationRequired={translationRequired}
          />
        </Accordion.Trigger>
        <Accordion.Panel>
          <SubtitleDragAndDrop
            adminCategoryId={safeAdminCategoryId}
            index={index}
            onSaveSubtitlesOrder={onSaveSubtitlesOrder}
            openInNewTab={openDocumentsInNewTab}
            selectedCountry={userPrefs.country.id ?? ''}
            showAdminFunctionality={showAdminFunctionality}
            subtitles={subtitles}
            userIsAdmin={userIsAdmin}
            sectionEditType={sectionEditType}
            setSectionEditType={setSectionEditType}
            subcategory={subcategory}
          />
          {userIsAdmin && showAdminFunctionality && (
            <>
              {!sectionEditType ? (
                <Button
                  color="secondary"
                  onClick={handleAddSection}
                  variant="text"
                  className="min-w-0! px-4! py-0!"
                >
                  <Icon
                    icon={IconPlus}
                    size="sm"
                    className="text-brand-navy! mr-2! cursor-pointer!"
                  />
                  <Typography
                    color="secondary"
                    data-testid="AddSubcategorySection"
                    variant="body1"
                  >
                    {t('Admin.addSection')}
                  </Typography>
                </Button>
              ) : (
                <>
                  <EditSection
                    addSectionFormIsValid={addSectionFormIsValid}
                    countryCode={countryCode}
                    handleAddSectionCancel={handleAddSectionCancel}
                    index={index}
                    onSaveSubtitle={onSaveSubtitle}
                    openInNewTab={openDocumentsInNewTab}
                    pendingSectionMutation={pendingSectionMutation}
                    setPendingSectionMutation={setPendingSectionMutation}
                    sectionEditType={sectionEditType}
                    setSectionEditType={setSectionEditType}
                  />
                </>
              )}
            </>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion.Root>
  );
};
